import {
  centsToBRL,
  microToTrimmedPercentDisplay,
} from '@/features/quotes/utils/rd-units.util';
import type {
  RdFranchiseRule,
  RdModalitySlug,
} from '@/services/quotes/rd-equipamentos/types';

/**
 * Descrição pt-BR de uma regra de franquia/POS — réplica literal de
 * `alba-portal-seguros-demo/modules/pdf_policy.py::franchise_text`
 * (`posType` → texto), convertendo `posPercent`/`minPercent` (micro) via
 * `microToTrimmedPercentDisplay` (zeros à direita aparados, como o `_rate_pct`
 * do demo) e `minValue`/`maxValue` (centavos) via `centsToBRL`.
 *
 * Vocabulário do PO (2026-09-03), e cada palavra responde a uma pergunta que
 * o texto antigo deixava aberta:
 *
 * - **POS**, não "Percentual" — é o nome do instituto (Participação
 *   Obrigatória do Segurado), e é assim que o quadro se chama na apólice.
 *   "Percentual" descrevia a FORMA de cálculo e competia com o "10%" da
 *   própria linha.
 * - **"do equipamento"** no mínimo percentual: `minPercent` é fração do VALOR
 *   do equipamento, enquanto `posPercent` é do PREJUÍZO. Sem dizer a base,
 *   "10% do prejuízo, mín. 1,5%" lia-se como 1,5% do prejuízo.
 * - **"por evento"** no mínimo em dinheiro: é piso por sinistro, não teto
 *   anual nem franquia única da apólice.
 *
 * As duas cópias mudam JUNTAS, senão a tela e o documento contam histórias
 * diferentes sobre a mesma regra. A apólice emitida não passa por aqui: quem
 * a renderiza é a Folheteria, no motor — e é por isso que a paridade importa.
 */
export function franchiseText(rule: RdFranchiseRule): string {
  if (rule.posType === 'exempt') return 'Isenta (sem franquia)';

  if (rule.posType === 'time') {
    return `Franquia — ${rule.timeDays ?? 0} dia(s)`;
  }

  if (rule.posType === 'percent') {
    const parts = [
      `${microToTrimmedPercentDisplay(rule.posPercent ?? 0)}% do prejuízo`,
    ];
    if (rule.minPercent) {
      parts.push(
        `mín. ${microToTrimmedPercentDisplay(rule.minPercent)}% do equipamento`
      );
    }
    if (rule.minValue) {
      parts.push(`mín. ${centsToBRL(rule.minValue)} por evento`);
    }
    if (rule.maxValue) {
      parts.push(`máx. ${centsToBRL(rule.maxValue)}`);
    }
    return `POS — ${parts.join(', ')}`;
  }

  return '—';
}

/**
 * Texto de franquia pronto, indexado por slug de cobertura, JÁ RESOLVIDO para
 * uma modalidade.
 *
 * A franquia deixou de ser uma tabela à parte e virou COLUNA da tabela de
 * coberturas (PO 2026-09-03) — o corretor lia LMI e prêmio num quadro e
 * descia até outro para saber a franquia da mesma linha. Uma coluna só
 * precisa de consulta por slug, então este mapa substituiu o antigo
 * `franchiseRows`, que montava linhas próprias com dedup e ordem.
 *
 * **O desempate é o mesmo do motor** (`FranchiseRule.resolve`): a regra COM
 * modalidade vence a global; sem nenhuma das duas, a cobertura não tem
 * franquia cadastrada. Proximidade de Água é o caso que obriga a isso — ela
 * só existe por modalidade (móvel 1,5%, estacionário e portátil 1,0%) e, sem
 * este desempate, saía sem franquia na tela enquanto o PDF da mesma apólice
 * imprimia o valor certo (QA de 2026-09-03).
 *
 * Efeito colateral bom da mudança: a tabela antiga listava franquia de
 * cobertura com LMI zero, enquanto a apólice — que só imprime as contratadas
 * — não listava. Como a coluna vive na tabela de coberturas, que já filtra
 * `lmi > 0`, tela e documento voltaram a concordar sem código extra.
 */
export function franchiseTextBySlug(
  franchiseRules: RdFranchiseRule[],
  modalitySlug?: RdModalitySlug
): Map<string, string> {
  const escolhida = new Map<string, RdFranchiseRule>();

  for (const rule of franchiseRules) {
    const especifica = rule.modalitySlug === modalitySlug;
    const global = rule.modalitySlug === undefined;
    if (!especifica && !global) continue;
    // A específica sobrescreve a global, e nunca o contrário — independe da
    // ordem em que o motor devolveu as linhas.
    const atual = escolhida.get(rule.coverageSlug);
    if (atual && atual.modalitySlug !== undefined && global) continue;
    escolhida.set(rule.coverageSlug, rule);
  }

  return new Map(
    [...escolhida].map(([slug, rule]) => [slug, franchiseText(rule)])
  );
}
